import { useTranslation } from 'react-i18next';
import { Toast, showErrorToUser, useConfirm } from '@pletly/ui';

import { useAcceptInvitationMutation } from '../__generated__/acceptInvitation.mutation.generated';
import { useRejectInvitationMutation } from '../__generated__/rejectInvitation.mutation.generated';
import { GetOrganisationDocument } from '../../../view/components/__generated__/organisation.generated';

export const useInvitationAction = () => {
	const { t } = useTranslation();
	const { confirm } = useConfirm();

	const [accept] = useAcceptInvitationMutation({
		refetchQueries: [GetOrganisationDocument],
	});
	const [reject] = useRejectInvitationMutation();

	const handleAction =
		(
			{
				id,
				requesterName,
				careRecipientName,
			}: { id: string; requesterName: string; careRecipientName: string },
			action: 'accept' | 'reject',
		) =>
		async () => {
			const shouldProceed = await confirm(
				t(`confirmations.${action}-invitation.title`),
				t(`confirmations.${action}-invitation.content`, { requesterName, careRecipientName }),
				{
					confirmProps: {
						color: action === 'reject' ? 'danger' : 'primary',
					},
				},
			);

			if (!shouldProceed) return;

			const mutation = action === 'accept' ? accept : reject;
			try {
				await mutation({
					variables: { id },
					update: (cache) => {
						const inviteRef = cache.identify({
							__typename: 'Invite',
							id: Number(id),
						});
						cache.evict({ id: inviteRef });
					},
				});

				Toast.show({
					text1: t(`toasts.success.invitation-${action}-title`),
					text2: t(`toasts.success.invitation-${action}-content`),
					type: action === 'accept' ? 'success' : 'error',
				});
			} catch (error) {
				showErrorToUser(error as Error);
			}
		};

	return { handleInvitationAction: handleAction };
};
